import React from "react";
import { cn } from "./ui/utils";

interface SkeletonListProps {
  count?: number;
  variant?: "card" | "row" | "compact" | "detail" | "home";
  className?: string;
}

function SkeletonPulse({ className }: { className?: string }) {
  return (
    <div className={cn("animate-pulse rounded-lg bg-gray-200/70", className)} />
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-4 pt-2">
      <div className="rounded-2xl bg-white/80 border border-white/40 p-4 space-y-4">
        <div className="flex items-start gap-3">
          <SkeletonPulse className="w-16 h-16 rounded-2xl flex-shrink-0" />
          <div className="flex-1 space-y-2 pt-1">
            <SkeletonPulse className="h-5 w-4/5" />
            <SkeletonPulse className="h-3.5 w-1/2" />
            <SkeletonPulse className="h-3 w-2/5" />
          </div>
        </div>
        <div className="flex gap-2">
          <SkeletonPulse className="h-7 w-20 rounded-full" />
          <SkeletonPulse className="h-7 w-16 rounded-full" />
          <SkeletonPulse className="h-7 w-24 rounded-full" />
        </div>
        <SkeletonPulse className="h-3 w-full" />
        <SkeletonPulse className="h-3 w-5/6" />
        <SkeletonPulse className="h-3 w-2/3" />
      </div>
      <div className="rounded-2xl bg-white/80 border border-white/40 p-4 space-y-3">
        <SkeletonPulse className="h-4 w-1/3" />
        <SkeletonPulse className="h-10 w-full rounded-xl" />
        <SkeletonPulse className="h-10 w-full rounded-xl" />
      </div>
      <div className="rounded-2xl bg-white/80 border border-white/40 p-4 space-y-3">
        <SkeletonPulse className="h-4 w-2/5" />
        <div className="flex gap-3">
          <SkeletonPulse className="w-14 h-14 rounded-xl" />
          <SkeletonPulse className="w-14 h-14 rounded-xl" />
          <SkeletonPulse className="w-14 h-14 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

function HomeSkeleton() {
  return (
    <div className="space-y-4">
      <SkeletonPulse className="w-full aspect-[16/9] rounded-none sm:rounded-2xl" />
      <div className="px-4 space-y-4">
        <div className="space-y-2">
          <SkeletonPulse className="h-6 w-3/4" />
          <SkeletonPulse className="h-3.5 w-1/2" />
        </div>
        <div className="grid grid-cols-5 gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5">
              <SkeletonPulse className="w-11 h-11 rounded-2xl" />
              <SkeletonPulse className="h-2.5 w-10" />
            </div>
          ))}
        </div>
        <SkeletonPulse className="h-4 w-1/3" />
        <SkeletonList count={3} variant="row" />
      </div>
    </div>
  );
}

export function SkeletonList({
  count = 4,
  variant = "card",
  className,
}: SkeletonListProps) {
  if (variant === "detail") {
    return (
      <div className={cn(className)}>
        <DetailSkeleton />
      </div>
    );
  }
  if (variant === "home") {
    return (
      <div className={cn(className)}>
        <HomeSkeleton />
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)} aria-busy="true" aria-live="polite">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i}>
          {variant === "card" && (
            <div className="rounded-2xl bg-white/80 border border-white/40 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <SkeletonPulse className="w-10 h-10 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <SkeletonPulse className="h-4 w-3/4" />
                  <SkeletonPulse className="h-3 w-1/2" />
                </div>
              </div>
              <SkeletonPulse className="h-3 w-full" />
              <SkeletonPulse className="h-3 w-2/3" />
            </div>
          )}
          {variant === "row" && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/80 border border-white/40">
              <SkeletonPulse className="w-10 h-10 rounded-xl flex-shrink-0" />
              <div className="flex-1 space-y-2 min-w-0">
                <SkeletonPulse className="h-4 w-2/3" />
                <SkeletonPulse className="h-3 w-1/2" />
              </div>
              <SkeletonPulse className="w-5 h-5 rounded flex-shrink-0" />
            </div>
          )}
          {variant === "compact" && (
            <div className="flex items-center gap-2 py-2">
              <SkeletonPulse className="w-8 h-8 rounded-lg" />
              <div className="flex-1 space-y-1.5">
                <SkeletonPulse className="h-3.5 w-1/2" />
                <SkeletonPulse className="h-2.5 w-1/3" />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
